import { FilterList } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import usePetViewOwnerId from "../../features/pets/hooks/usePetViewOwnerId";
import { DocumentAppointmentFilterContext } from "../../contexts/FilterAppointmentsWithDocuments";
import {
  ATTACH_FILE_TO_PET,
  CLINIC_CATEGORIES_DROPDOWN,
  CLINIC_CATEGORIES_MASTER,
} from "../../enums/ApiPaths";
import ToggleFileUpload from "../../features/appointments/context/ToggleFileUpload";
import useUser from "../../hooks/useUser";
import AttachFileDialog from "../AttachFileDialog";
import SimpleAlert from "../display/SimpleAlert";
import DateRange from "../filter/DateRange";
import SearchBar from "../filter/SearchBar";
import useGetCategoreis from "../form/fileUpload/hooks/useGetCategoreis";

export const petDocumentsParams = {
  search: "q",
  startDate: "from",
  endDate: "to",
  category: "cat",
  fileExtension: "extension",
};

export default function AppointmentsWithDocumentsFilter() {
  const {
    data,
    getCategoryFilter,
    handleFilterCategory,
    handleFilterFileExtension,
    handleFilterTitle,
    reset,
    getFilterFileExtensions,
    isLoading,
  } = React.useContext(DocumentAppointmentFilterContext);
  const { roles, currentClinic } = useUser();
  const { id: petId } = useParams();
  const ownerId = usePetViewOwnerId();

  const url = roles?.master
    ? CLINIC_CATEGORIES_MASTER
    : CLINIC_CATEGORIES_DROPDOWN;

  const id = roles?.master ? Number(ownerId) : Number(currentClinic.id);
  const categories = useGetCategoreis(url, id, id);

  const { t } = useTranslation();
  const uploadUrl = `${ATTACH_FILE_TO_PET}${petId}`;

  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [searchParams] = useSearchParams();
  // Leftover "null"/"Invalid Date" values from the date picker aren't active filters
  const activeFilterCount = Object.values(petDocumentsParams).filter((param) => {
    const value = searchParams.get(param);
    return Boolean(value) && value !== "null" && value !== "Invalid Date";
  }).length;

  function handleReset() {
    reset();
  }

  return (
    <Fragment>
      <Stack
        sx={{ p: 2 }}
        spacing={2}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-start"}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterList />}
          onClick={() => setFiltersOpen(true)}
          data-testid="pet.documents.filter-button"
        >
          {t("Filter")}
          {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </Button>
        <ToggleFileUpload>
          <AttachFileDialog endpoint={uploadUrl} asButton />
        </ToggleFileUpload>
        <Button
          color="error"
          variant="outlined"
          size="small"
          onClick={() => handleReset()}
        >
          {t("Reset")}
        </Button>
      </Stack>


      <Dialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        keepMounted
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2,
            px: 3,
          }}
        >
          <Typography variant="h6" component="h2" fontWeight={600}>
            {t("Filter")}
          </Typography>
          <IconButton
            onClick={() => setFiltersOpen(false)}
            color="error"
            title={t("Close")}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.6 }} />
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              {isLoading ? (
                <Skeleton height={"100%"} />
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>{t("FileExtensions")}</InputLabel>
                  <Select
                    size="small"
                    defaultValue={""}
                    value={getFilterFileExtensions()}
                    onChange={(event) =>
                      handleFilterFileExtension(event.target.value)
                    }
                    label={t("FileExtensions")}
                  >
                    <MenuItem value="">
                      <em>{t("Reset")}</em>
                    </MenuItem>
                    {data?.extensions?.map((extension, index) => (
                      <MenuItem value={extension} key={index}>
                        {String(extension).toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateRange
                paramsEnd={petDocumentsParams?.endDate}
                paramsStart={petDocumentsParams?.startDate}
                divider={false}
                spacing={2}
                margin={0}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={categories.translatedCategories.length == 0 ? 12 : 6}
            >
              <SearchBar
                key={"filter_documents_by_title_search_bar"}
                lsVal={petDocumentsParams?.search}
                label={t("SearchByTitle")}
                margin={0}
                getData={(value) => handleFilterTitle(value)}
              />
            </Grid>
            <CategoryPicker
              categories={categories}
              isLoading={isLoading}
              getCategoryFilter={getCategoryFilter}
              handleFilterCategory={handleFilterCategory}
            />
          </Grid>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}

function CategoryPicker({
  isLoading,
  getCategoryFilter,
  handleFilterCategory,
  categories,
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return <Skeleton height={"100%"} />;
  } else if (categories?.error) {
    return (
      <SimpleAlert
        severity={"error"}
        message={"FailedToFetchDocumentCategoryList"}
      />
    );
  } else if (categories.translatedCategories.length == 0) {
    return null;
  }

  return (
    <Grid item xs={12} sm={6}>
      <FormControl fullWidth size="small">
        <InputLabel>{t("Category")}</InputLabel>
        <Select
          defaultValue={""}
          value={getCategoryFilter()}
          onChange={(event) => handleFilterCategory(event.target.value)}
          label={t("Category")}
        >
          <MenuItem value="">
            <em>{t("Reset")}</em>
          </MenuItem>
          {categories?.translatedCategories?.map((category) => (
            <MenuItem value={category?.id} key={category?.id}>
              {category?.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );
}
